import React, { Component, SyntheticEvent } from 'react';

import styled from 'styled-components';
import {
    DebugInfo,
    DebugInfoProps,
    DebugInfoRow,
    DeviceDetectionUtils,
    FrameCollectionModule,
    Label,
    LabelProps,
    Logger,
    MissingBillingUserInfoAlertMessage,
    ModalWizard,
    PageContentLoader,
    UiUserNotificationManager,
    UiUserNotificationTypes,
    UiUserNotificationSettingSpec,
    PageNotificationColorCode,
    PageNotification,
    SubText
} from 'UI-Dash-Commons-UI';

import FrameMessagesAPI from '../utils/FrameMessages';
import MenuSection from '../../../types/MenuSection';

import Section1 from '../../MaxWelcomeModal/components/Section1';
import Section2 from '../../MaxWelcomeModal/components/Section2';
import Section3 from '../../MaxWelcomeModal/components/Section3';
import Section4 from '../../MaxWelcomeModal/components/Section4';
import AppHistory from '../../../utils/AppHistory';

import UserGrowlsComponent from './UserGrowlsComponent';
import LegalModalComponent from '../../../../../UI-Dash-App-Discovery-Reporting-UI/src/modules/EcommerceDocumentation/components/LegalModalComponent';
import { isEcommUser } from '../../../../../UI-Dash-Internal-UI/src/modules/Users/components/UserEditPage/GeneralSettings/AccountSettingsComponent';
import RedirectModalComponent from './RedirectModalComponent';

const TAG = 'FrameCollectionComponent';

const INITIAL_CONTENT_HEIGHT = 500;
const INITIAL_PARENT_FRAME_HEIGHT_OFFSET = 48;
const PARENT_FRAME_HEIGHT_WITH_NOTIFICATION_OFFSET = 210;
const HTML_REVISION = 3;

type FrameCollectionComponentProps = {
    activateSection: ( sectionSpec: MenuSection, pushUrl: boolean, updateTimeStamp: boolean ) => void,
    contentHeight: number,
    focusedSectionId: string,
    focusedSectionSpec: MenuSection,
    iframeSectionSpec: MenuSection,
    isDebugInfoOpen: boolean,
    isLeftSideMenuPinned: boolean,
    lastLoadTimeStamp: string,
    userRole: number,
    location: Location,
    showNotification: ( notificationMessage: string, notificationType: string ) => void,
    updateActiveSection: ( newUrl: string, pushUrl?: boolean, updateTimeStamp?: boolean ) => void,
    updateBrowserLocation: ( newUrl: string ) => void,
    replaceBrowserLocation: ( newUrl: string ) => void,
    updateContentHeight: ( newHeight: number ) => void
};

type FrameCollectionComponentState =
    {
        debugInfo: Array<DebugInfoRow> | null,
        iframeHeight: number,
        parentFrameHeightOffset: number,
        isFailedToLoad: boolean,
        isLoading: boolean,
        iframeUrl: string,
        uiUserNotificationSettingSpec: UiUserNotificationSettingSpec | null
    };

export default class FrameCollectionComponent extends Component<FrameCollectionComponentProps, FrameCollectionComponentState>
{
    private currentIFrameRef: React.RefObject<HTMLIFrameElement> | null = null;

    constructor( props: FrameCollectionComponentProps )
    {
        super( props );

        this.state =
            {
                iframeHeight: 0,
                parentFrameHeightOffset: INITIAL_PARENT_FRAME_HEIGHT_OFFSET,
                isLoading: true,
                isFailedToLoad: false,
                debugInfo: [],
                iframeUrl: '',
                uiUserNotificationSettingSpec: null,
            };
    }

    isValidAndNewHeight( newHeight?: number | null ): boolean
    {
        return ( typeof newHeight !== 'undefined' ) && ( newHeight !== this.props.contentHeight );
    }

    redirectToUrl( url ): void
    {
        const { activateSection } = this.props;

        const sectionSpec: MenuSection = new MenuSection(
            {
                externalUrl: url,
                sectionUrlType: 'same_tab'
            }
        );

        activateSection( sectionSpec, true, false );
    }

    redirectToLogin(): void
    {
        this.redirectToUrl( '/' );
    }

    redirectToLanding( url = '/o' ): void
    {
        this.redirectToUrl( url );
    }

    maybeAttachToCurrentFrame(): void
    {
        if ( this.currentIFrameRef && this.currentIFrameRef.current )
        {
            const iFrameDom: HTMLIFrameElement = this.currentIFrameRef.current;

            if ( iFrameDom.contentWindow )
            {
                FrameMessagesAPI.registerFrame( this.props.focusedSectionId, iFrameDom.contentWindow );
            }
        }
    }

    componentDidMount(): void
    {
        FrameMessagesAPI.startListening();
        this.maybeAttachToCurrentFrame();

        FrameMessagesAPI.registerMessageProcessor( 'height_change', ( message: FrameCollectionModule.types.HeightChangeMessage ) =>
        {
            const { updateContentHeight } = this.props;
            const newHeight: number | null | undefined = message.height;

            if ( this.isValidAndNewHeight( newHeight ) )
            {
                Logger.debug( TAG, 'iFrame  has changed its height to ' + newHeight );
                updateContentHeight( newHeight as number );
            }
            else
            {
                Logger.warn( TAG, 'iFrame dispatched zero height' );
            }
        } );

        FrameMessagesAPI.registerMessageProcessor( 'redirect_to_login', () =>
        {
            this.redirectToLogin();
        } );

        FrameMessagesAPI.registerMessageProcessor( 'redirect_to_landing', () =>
        {
            this.redirectToLanding();
        } );

        FrameMessagesAPI.registerMessageProcessor( 'show_notification', ( message: FrameCollectionModule.types.NotificationMessage ) =>
        {
            this.props.showNotification( message.notificationMessage, message.notificationType );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'location_change', ( message: FrameCollectionModule.types.LocationChangeMessage ) =>
        {
            Logger.info( TAG, 'Iframe location has changed' );

            const { updateActiveSection, updateBrowserLocation } = this.props;
            const newUrl: string = this.buildBrowserLocation( message.location );

            updateBrowserLocation( newUrl );
            updateActiveSection( newUrl );

            this.setState( {
                isLoading: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'location_change_with_timestamp_change', ( message: FrameCollectionModule.types.LocationChangeMessage ) =>
        {
            const { updateActiveSection, updateBrowserLocation } = this.props;
            const newUrl: string = this.buildBrowserLocation( message.location );

            updateBrowserLocation( newUrl );

            updateActiveSection( newUrl, true, true );

            this.setState( {
                isLoading: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'location_change_no_section_change', ( message: FrameCollectionModule.types.LocationChangeMessage ) =>
        {
            Logger.info( TAG, 'Iframe location has changed without a need for changing active section' );

            const { updateBrowserLocation } = this.props;
            const newUrl: string = this.buildBrowserLocation( message.location );

            updateBrowserLocation( newUrl );

            this.setState( {
                isLoading: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'location_change_replace', ( message: FrameCollectionModule.types.LocationChangeMessage ) =>
        {
            Logger.info( TAG, 'Iframe location has been replaced' );

            const { updateActiveSection, replaceBrowserLocation } = this.props;
            const newUrl: string = this.buildBrowserLocation( message.location );

            replaceBrowserLocation( newUrl );
            updateActiveSection( newUrl );

            this.setState( {
                isLoading: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'location_change_replace_no_section_change', ( message: FrameCollectionModule.types.LocationChangeMessage ) =>
        {
            Logger.info( TAG, 'Iframe location has been replaced without a need for changing active section' );

            const { replaceBrowserLocation } = this.props;
            const newUrl: string = this.buildBrowserLocation( message.location );

            replaceBrowserLocation( newUrl );

            this.setState( {
                isLoading: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'show_debug_info', ( message: FrameCollectionModule.types.DebugInfoMessage ) =>
        {
            this.setState( ( prevState: FrameCollectionComponentState ) => ( {
                ...prevState,
                debugInfo: message.debugInfo
            } ) );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'loaded', () =>
        {
            Logger.info( TAG, 'Iframe loaded' );

            // Update state
            this.setState( {
                isLoading: false,
                isFailedToLoad: false
            } );
        } );

        FrameMessagesAPI.registerMessageProcessor( 'ui_user_notification', ( message: FrameCollectionModule.types.UiUserNotificationMessage ) =>
        {
            if ( message.uiUserNotificationSpec )
            {
                Logger.info( TAG, 'UI User Notification received' );

                this.setState( {
                    uiUserNotificationSettingSpec: message.uiUserNotificationSpec,
                } );

            }
        } );
    }

    componentWillUnmount(): void
    {
        FrameMessagesAPI.stopListening();
    }

    static getDerivedStateFromProps( nextProps: FrameCollectionComponentProps, prevState: FrameCollectionComponentState )
    {
        if ( !nextProps.iframeSectionSpec && nextProps.focusedSectionSpec )
        {
            return {
                iframeUrl: nextProps.focusedSectionSpec.sectionUrl
            };
        }

        return null;
    }

    componentDidUpdate( prevProps: FrameCollectionComponentProps ): void
    {
        this.maybeAttachToCurrentFrame();

        if ( this.props.lastLoadTimeStamp !== prevProps.lastLoadTimeStamp )
        {
            Logger.debug( TAG, 'Focused section ID updated to: ' + this.props.focusedSectionId );
            Logger.debug( TAG, 'Following URL will be requested: \'' + this.props.focusedSectionSpec.sectionUrl + '\'...' );

            if ( prevProps.focusedSectionId )
            {
                let iFrameDom: HTMLIFrameElement | null = null;

                if ( this.currentIFrameRef )
                {
                    iFrameDom = this.currentIFrameRef.current;
                }
                if ( this.props.iframeSectionSpec && !this.state.isFailedToLoad && iFrameDom && iFrameDom.contentWindow )
                {
                    // if its within the same module, let client history handle the navigate and just update the url
                    AppHistory.replace( this.props.iframeSectionSpec.externalUrl );
                }
                else if ( iFrameDom && iFrameDom.contentWindow )
                {
                    // hard reload if its cross module or for adsui
                    iFrameDom.contentWindow.location.href = this.getUrlWithForceRefresh( this.props.focusedSectionSpec.sectionUrl );
                }

                FrameMessagesAPI.removeRegisteredFrame( prevProps.focusedSectionId );
            }

            // Re-set frame height
            const { updateContentHeight } = this.props;
            updateContentHeight( INITIAL_CONTENT_HEIGHT );

            // Update state
            if ( this.props.location.pathname !== prevProps.location.pathname
                || this.props.location.search !== prevProps.location.search )
            {
                this.setState( {
                    isLoading: true,
                    isFailedToLoad: false
                } );
            }
        }
    }

    handleFrameLoaded = ( event: SyntheticEvent<HTMLIFrameElement> ) =>
    {
        const { updateContentHeight, updateBrowserLocation, updateActiveSection, iframeSectionSpec } = this.props;

        // Get the iFrame dom
        const iFrameDom: HTMLIFrameElement = event.currentTarget as HTMLIFrameElement;

        Logger.debug( TAG, 'Section content loaded for ' + iFrameDom.id + ')' );

        // Check if this is a real page
        let divCount = 0;

        if ( iFrameDom.contentDocument )
        {
            divCount = iFrameDom.contentDocument.getElementsByTagName( 'script' ).length;
        }

        if ( divCount === 0 )
        {
            Logger.error( TAG, 'Returned page has 0 DIV elements, assuming it is an error' );
            this.handleFrameFailedToLoad( event );

            return;
        }

        if ( iFrameDom.contentWindow )
        {
            const currentLocationUrl = iFrameDom.contentWindow.location;
            const currentLocationUrlString = this.buildBrowserLocation( currentLocationUrl );
            // Get the current iframe url and update the browser url accordingly
            updateBrowserLocation( currentLocationUrlString );

            // check whether the path changed
            const prevLocationUrl = new URL( iFrameDom.src );

            if ( !iframeSectionSpec && prevLocationUrl.pathname !== currentLocationUrl.pathname )
            {
                updateActiveSection( currentLocationUrlString );
            }

            // Add the a listener for hash changes
            const newHeight: number = this.state.iframeHeight;

            if ( this.isValidAndNewHeight( newHeight ) )
            {
                Logger.debug( TAG, 'Newly loaded iFrame has changed height to ' + newHeight );

                updateContentHeight( newHeight );
            }

            // Check we loaded top-level UI in the frame
            if ( iFrameDom.contentWindow[ 'al_is_top_level' ] )
            {
                this.redirectToLanding( '' );
            }
        }

        this.setState( {
            isLoading: false,
            isFailedToLoad: false
        } );
    };

    buildBrowserLocation = ( iFrameLocation: Location ) =>
    {
        if (
            /^\//.test( iFrameLocation.pathname )
            && !( /^\/o\//.test( iFrameLocation.pathname ) )
        )
        {
            return '/o' + iFrameLocation.pathname + iFrameLocation.search + iFrameLocation.hash;
        }
        else
        {
            return iFrameLocation.pathname + iFrameLocation.search + iFrameLocation.hash;
        }
    };

    handleFrameFailedToLoad = ( event: SyntheticEvent<HTMLIFrameElement> ) =>
    {
        const iFrameDom: HTMLIFrameElement = event.currentTarget as HTMLIFrameElement;

        Logger.error( TAG, 'Failed to load content for ' + iFrameDom.id + '' );

        this.setState( {
            isLoading: false,
            isFailedToLoad: true
        } );
    };

    isPageNotificationVisible = ( isVisible: boolean ): void =>
    {

        if ( isVisible )
        {
            this.setState( {
                parentFrameHeightOffset: PARENT_FRAME_HEIGHT_WITH_NOTIFICATION_OFFSET
            } );
        }
        else
        {
            this.setState( {
                parentFrameHeightOffset: INITIAL_PARENT_FRAME_HEIGHT_OFFSET
            } );
        }
    };

    onCloseModal = async () =>
    {

        const { uiUserNotificationSettingSpec } = this.state;

        if ( uiUserNotificationSettingSpec )
        {
            try
            {
                uiUserNotificationSettingSpec.disabled = true;

                await UiUserNotificationManager.saveNotification( uiUserNotificationSettingSpec );
            }
            catch ( error )
            {
                Logger.error( TAG, "Something went wrong with saving ui user notification " + error );
            }
        }
    };

    getUrlWithForceRefresh = ( url: string ) =>
    {
        if ( !url.includes( "?r=" ) )
        {
            // check if query params exists, then add r=HTML_REVISION
            const queryParamIndex = url.indexOf( '?' );
            if ( queryParamIndex > -1 )
            {
                return url.slice( 0, queryParamIndex + 1 ) + `r=${HTML_REVISION}&` + url.slice( queryParamIndex + 1 );
            }

            // check if hash string exists, then insert before hash
            const hashIndex = url.indexOf( '#' );
            if ( hashIndex > -1 )
            {
                return url.slice( 0, hashIndex ) + `?r=${HTML_REVISION}` + url.slice( hashIndex );
            }

            return url + `?r=${HTML_REVISION}`;
        }

        // always bump to the latest revision
        return url.replace( /r=\d+/, `r=${HTML_REVISION}` );
    }

    render()
    {
        const { contentHeight, isLeftSideMenuPinned, isDebugInfoOpen, focusedSectionSpec } = this.props;
        const {
            isLoading,
            isFailedToLoad,
            parentFrameHeightOffset,
            iframeUrl,
            uiUserNotificationSettingSpec
        } = this.state;

        const newIframeUrl = this.getUrlWithForceRefresh( iframeUrl );

        const focusedSectionId: string = focusedSectionSpec && focusedSectionSpec.id;

        if ( !focusedSectionId )
        {
            return (
                <PageContentLoader/>
            );
        }

        const isIFrameFixRequired: boolean = DeviceDetectionUtils.isIOSMobileDevice();

        const showPaymentsNotificationAccountsBillingPage20240501 = (
            this.props.location.pathname.includes( '/o/account' ) && this.props.location.hash.includes( '#billing_history' )
            && !isLoading
            && !isFailedToLoad
            && ( new Date() < new Date( '2024-05-01T00:00:00.000Z' ) )
        );

        const showPaymentDetailsNotificationEcommerceCampaignsPage = (
            this.props.location.pathname.includes( '/o/webCampaigns' )
            && !this.props.location.pathname.includes( '/o/webCampaigns/' )
            && !isLoading
            && !isFailedToLoad
        );

        const showLegalModal = isEcommUser( this.props.userRole ) && this.props.location.pathname.includes( '/o/account' ) && this.props.location.hash.includes( '#keys' );
        const showRedirectModal = isEcommUser( this.props.userRole );

        this.currentIFrameRef = React.createRef<HTMLIFrameElement>();

        return (
            <SectionFrameContent
                isLeftSideMenuPinned={isLeftSideMenuPinned}
            >
                {uiUserNotificationSettingSpec && uiUserNotificationSettingSpec.name == UiUserNotificationTypes.WELCOME_MEDIATION &&
                    <ModalWizard
                        sections={[ <Section1/>, <Section2/>, <Section3/>, <Section4 onClick={this.onCloseModal}/> ]}
                        isModalVisible={true} enableModalClosing={false} onModalClose={this.onCloseModal}/>
                }
                {<UserGrowlsComponent />}
                <PageContentLoader
                    isVisible={isLoading}
                />
                {
                    showPaymentsNotificationAccountsBillingPage20240501 &&
                    <PageNotificationWrapper
                        isVisible={showPaymentsNotificationAccountsBillingPage20240501}>
                        <PageNotification colorCode={PageNotificationColorCode.LIGHT_BLUE}
                                          localStorageKey={'payments_notification_20240501'}>
                            <SubText>
                                Effective <b>May 1st 2024</b>, AppLovin's minimum payment threshold for earnings
                                generated through the
                                AppLovin network will be updated from $20 to $100.
                            </SubText>
                        </PageNotification>
                    </PageNotificationWrapper>
                }
                {
                    showPaymentDetailsNotificationEcommerceCampaignsPage &&
                        <MissingBillingUserInfoAlertMessageWrapper>
                            <MissingBillingUserInfoAlertMessage
                                labelText={'Looks like you have not entered your payment details. To complete the account creation process, enter your '}
                                anchorText={'payment information.'}
                            />
                        </MissingBillingUserInfoAlertMessageWrapper>
                }
                <ErrorMessage
                    text={'Unable to load page'}
                    isVisible={isFailedToLoad}
                />
                {showLegalModal && <LegalModalComponent />}
                {showRedirectModal && <RedirectModalComponent />}
                <ContentIframe
                    src={newIframeUrl}
                    id={focusedSectionId}
                    frameBorder={0}

                    contentHeight={contentHeight}
                    parentFrameHeightOffset={parentFrameHeightOffset}
                    isIFrameFixRequired={isIFrameFixRequired}

                    onLoad={this.handleFrameLoaded}
                    onError={this.handleFrameFailedToLoad}

                    isVisible={!isLoading && !isFailedToLoad}

                    ref={this.currentIFrameRef as React.RefObject<any>}
                />
                {
                    isDebugInfoOpen &&
                    <DebugInfoTable
                        isLeftSideMenuPinned={this.props.isLeftSideMenuPinned}
                        data={this.state.debugInfo}
                    />
                }
            </SectionFrameContent>
        );
    }
}


const SectionFrameContent = styled<{ isLeftSideMenuPinned: boolean }, 'section'>( 'section' )`
    margin-left: 0;
    margin-left: ${props => ( !props.isLeftSideMenuPinned ) && '74px'};

    max-width: calc(100vw - 255px);
    max-width: ${props => ( !props.isLeftSideMenuPinned ) && 'calc(100vw - 74px)'};

    background-color: ${props => props.theme.colors.white};
`;

const ContentIframe = styled<{
    contentHeight: number,
    parentFrameHeightOffset: number,
    isIFrameFixRequired: boolean,
    isVisible: boolean,
    onLoad: ( event: SyntheticEvent<HTMLIFrameElement> ) => void,
    onError: ( event: SyntheticEvent<HTMLIFrameElement> ) => void
}, 'iframe'>( 'iframe' )`

    display: none;
    display: ${props => props.isVisible && 'block'};

    max-width: 100%;
    min-width: 100%;
    width: 0px;

    max-height: 100%;
    height: ${props => props.isIFrameFixRequired && ( props.contentHeight + 'px' )};
    min-height: ${( props ) => `calc(100vh - ${props.parentFrameHeightOffset}px)`};

    padding: 0;

    overflow: scroll;
    -webkit-overflow-scrolling: touch;
`;


const ErrorMessage = styled<{ isVisible: boolean } & LabelProps>( ( { isVisible, ...rest } ) =>
    <Label {...rest} /> )`
    display: none;
    display: ${props => props.isVisible && 'block'};
    width: 30%;

    margin: 20% auto auto;

    color: ${props => props.theme.colors.orange};
    text-align: center;
`;

const DebugInfoTable = styled<{ isLeftSideMenuPinned: boolean } & DebugInfoProps>( ( {
                                                                                         isLeftSideMenuPinned,
                                                                                         ...rest
                                                                                     } ) =>
    <DebugInfo {...rest}/> )`
    position: fixed;
    top: 48px;

    width: ${props => !props.isLeftSideMenuPinned ? 'calc(100vw - 89px)' : 'calc(100vw - 265px)'};
    max-height: calc(100vh - 48px);
    height: 100%;

    background-color: rgb(27, 27, 27, 0.6);

    overflow-y: auto;
`;

const PageNotificationWrapper = styled<{ isVisible: boolean }, 'div'>( 'div' )`

    display: none;
    display: ${props => props.isVisible && 'block'};
`;

const MissingBillingUserInfoAlertMessageWrapper = styled( 'div' )`
    margin: 0 20px;
`;
